import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { ArrowLeft, ExternalLink, LifeBuoy, MapPin, Star } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Countdown, Rows, Section, StageBadge, StageProgress } from "@/components/portal/portal-ui";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  getPickupPoint,
  googleMapsUrl,
  mapEmbedUrl,
  OTOPENI_NOTE,
  wazeUrl,
} from "@/data/locations";
import type { BookingStage } from "@/lib/booking-stages";
import { stageLabel } from "@/lib/booking-stages";
import { getMyBooking, sendBookingRequest } from "@/lib/bookings.functions";

const REVIEW_URL = "https://g.page/r/Cd-sWtA9K3xsEBM/review";

const REQUEST_KINDS = [
  "Extend rental",
  "Contact support",
  "Report an issue",
  "Accident assistance",
] as const;

const GUIDE = [
  {
    title: "Things to do",
    items: [
      "Palace of the Parliament — book the guided tour in advance",
      "Old Town (Lipscani) — walkable, park outside and take a short taxi",
      "Village Museum & Herăstrău Park — easy free parking",
    ],
  },
  {
    title: "Restaurants",
    items: [
      "Caru' cu Bere — classic Romanian, Old Town",
      "Hanu' lui Manuc — historic courtyard",
      "Șosea / Dorobanți area — modern bistros with valet parking",
    ],
  },
  {
    title: "Parking",
    items: [
      "Central Bucharest is paid parking — use the Parking Bucuresti app",
      "Malls (AFI, Băneasa, Unirea) offer cheap covered parking",
      "Never leave valuables visible in the car",
    ],
  },
  {
    title: "Day trips",
    items: [
      "Peleș Castle & Sinaia — 2h on the DN1",
      "Brașov & Bran Castle — 3h, best started early",
      "Transfăgărășan (summer only) — full-day drive",
    ],
  },
];

export const Route = createFileRoute("/_authenticated/portal/$id")({
  head: () => ({
    meta: [
      { title: "My Booking | Car Rental Bucharest" },
      {
        name: "description",
        content:
          "Your reservation details: live status, countdown, pickup and return instructions, support and the Bucharest guide.",
      },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "My Booking | Car Rental Bucharest" },
      { property: "og:description", content: "Your Bucharest car rental reservation details." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: BookingDetail,
});

function BookingDetail() {
  const { id } = Route.useParams();
  const queryClient = useQueryClient();
  const fetchBooking = useServerFn(getMyBooking);
  const submitRequest = useServerFn(sendBookingRequest);
  const [kind, setKind] = useState<(typeof REQUEST_KINDS)[number]>("Contact support");
  const [message, setMessage] = useState("");

  const query = useQuery({
    queryKey: ["my-booking", id],
    queryFn: () => fetchBooking({ data: { id } }),
  });

  const requestMutation = useMutation({
    mutationFn: () => submitRequest({ data: { id, kind, message } }),
    onSuccess: () => {
      setMessage("");
      queryClient.invalidateQueries({ queryKey: ["my-booking", id] });
      toast.success("Message sent", { description: "Our team will reply by email shortly." });
    },
    onError: () => toast.error("Could not send your message. Please try WhatsApp."),
  });

  if (query.isLoading) {
    return (
      <>
        <main className="container-page py-24 text-muted-foreground">Loading your booking…</main>
      </>
    );
  }

  if (!query.data) {
    return (
      <>
        <main className="container-page py-24">
          <h1 className="font-display text-2xl font-bold">Booking not found</h1>
          <p className="mt-3 text-sm text-muted-foreground">
            This reservation does not belong to your account.
          </p>
          <Button asChild className="mt-6">
            <Link to="/portal">Back to my bookings</Link>
          </Button>
        </main>
      </>
    );
  }

  const b = query.data.booking;
  const history = query.data.history;
  const stage = b.stage as BookingStage;
  const pickupPoint = b.pickup_type === "custom" ? null : getPickupPoint(b.pickup_type);
  const dropoffPoint = b.dropoff_type === "custom" ? null : getPickupPoint(b.dropoff_type);
  const days = Math.max(
    1,
    Math.round(
      (new Date(b.return_date).getTime() - new Date(b.pickup_date).getTime()) / 86_400_000,
    ),
  );
  const extras: [string, React.ReactNode][] = [];
  if (Number(b.delivery_fee) > 0) extras.push(["Custom location delivery", `€${Number(b.delivery_fee)}`]);
  if (Number(b.airport_fee) > 0) extras.push(["Airport transfer", `€${Number(b.airport_fee)}`]);

  return (
    <>
      <main className="container-page py-10 sm:py-14">
        <Link
          to="/portal"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" /> My bookings
        </Link>

        <header className="mt-4 flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold">
              Reference {b.id.slice(0, 8).toUpperCase()}
            </p>
            <h1 className="mt-2 font-display text-3xl font-bold sm:text-4xl">{b.car_name}</h1>
          </div>
          <StageBadge stage={stage} className="mt-2" />
        </header>

        <div className="mt-6">
          <StageProgress stage={stage} />
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <Countdown label="Pickup in" date={b.pickup_date} doneLabel="Pickup day" />
          <Countdown label="Return in" date={b.return_date} doneLabel="Return due" />
        </div>

        <div className="mt-6 grid gap-5 lg:grid-cols-2">
          <Section title="My reservation">
            <Rows
              rows={[
                ["Car", b.car_name],
                ["Pickup", `${b.pickup_date} · ${b.pickup_location}`],
                ["Return", `${b.return_date} · ${b.dropoff_location ?? b.pickup_location}`],
                ["Duration", `${days} ${days === 1 ? "day" : "days"}`],
                ["Rate", b.price_per_day ? `€${Number(b.price_per_day)} / day` : "—"],
                ...extras,
                [
                  "Price",
                  b.total_estimate ? (
                    <span className="text-gold">€{Number(b.total_estimate)}</span>
                  ) : (
                    "To confirm"
                  ),
                ],
                ["Payment", "Paid at pickup — no deposit, no card required"],
              ]}
            />
          </Section>

          <Section title="Pickup">
            {pickupPoint ? (
              <>
                <p className="flex items-start gap-2">
                  <MapPin className="mt-0.5 size-4 shrink-0 text-gold" />
                  {pickupPoint.address}
                </p>
                {b.pickup_type === "otopeni" ? (
                  <p className="mt-3 rounded-lg border border-gold/30 bg-gold/5 p-3 text-xs text-foreground">
                    {OTOPENI_NOTE}
                  </p>
                ) : null}
                <iframe
                  title="Pickup location map"
                  src={mapEmbedUrl(pickupPoint.lat, pickupPoint.lng)}
                  loading="lazy"
                  className="mt-4 h-48 w-full rounded-lg border border-border"
                />
                <div className="mt-3 flex gap-2">
                  <Button asChild size="sm" variant="outline">
                    <a href={googleMapsUrl(pickupPoint.lat, pickupPoint.lng)} target="_blank" rel="noreferrer">
                      Google Maps <ExternalLink className="ml-1.5 size-3.5" />
                    </a>
                  </Button>
                  <Button asChild size="sm" variant="outline">
                    <a href={wazeUrl(pickupPoint.lat, pickupPoint.lng)} target="_blank" rel="noreferrer">
                      Waze <ExternalLink className="ml-1.5 size-3.5" />
                    </a>
                  </Button>
                </div>
              </>
            ) : (
              <p>We deliver the car to: {b.custom_pickup_address ?? b.pickup_location}</p>
            )}
            <p className="mt-4 text-xs">
              Bring your driving licence (held 1+ year) and passport or ID. Allow 15 minutes for the
              handover. Support: <a className="text-gold" href="https://wa.me/40738792499">WhatsApp +40 738 792 499</a>.
            </p>
          </Section>

          <Section title="Return">
            <p className="flex items-start gap-2">
              <MapPin className="mt-0.5 size-4 shrink-0 text-gold" />
              {dropoffPoint ? dropoffPoint.address : (b.custom_dropoff_address ?? b.dropoff_location)}
            </p>
            <Rows
              rows={[
                ["Return date", b.return_date],
                ["Time", "Same time as pickup unless agreed otherwise"],
                ["Fuel", "Return with the same fuel level as at pickup"],
                ["Check", "Quick joint inspection, around 15 minutes"],
              ]}
            />
          </Section>

          <Section title="During your rental">
            <p>
              Need something? Send us a message and we will reply by email. For an accident or
              breakdown, call or WhatsApp us immediately at +40 738 792 499.
            </p>
            <div className="mt-4 space-y-3">
              <Select value={kind} onValueChange={(v) => setKind(v as typeof kind)}>
                <SelectTrigger aria-label="Request type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REQUEST_KINDS.map((k) => (
                    <SelectItem key={k} value={k}>
                      {k}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Tell us what you need…"
                rows={4}
                aria-label="Your message"
              />
              <Button
                onClick={() => requestMutation.mutate()}
                disabled={message.trim().length < 5 || requestMutation.isPending}
                className="w-full"
              >
                <LifeBuoy className="mr-2 size-4" />
                {requestMutation.isPending ? "Sending…" : "Send to our team"}
              </Button>
            </div>
          </Section>
        </div>

        {["completed", "review_pending", "completed_reviewed"].includes(stage) ? (
          <section className="mt-6 rounded-xl border border-gold/40 bg-gold/5 p-6 text-center">
            <Star className="mx-auto size-6 text-gold" />
            <h2 className="mt-3 font-display text-xl font-bold">How was your rental?</h2>
            <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
              We are a small local business — a 30-second Google review genuinely helps other
              travellers find us.
            </p>
            <Button asChild className="mt-4">
              <a href={REVIEW_URL} target="_blank" rel="noreferrer">
                Leave a Google review
              </a>
            </Button>
          </section>
        ) : null}

        <section className="mt-6 grid gap-5 lg:grid-cols-2">
          <Section title="Bucharest guide">
            <div className="grid gap-4 sm:grid-cols-2">
              {GUIDE.map((g) => (
                <div key={g.title}>
                  <p className="text-xs font-semibold uppercase tracking-widest text-gold">
                    {g.title}
                  </p>
                  <ul className="mt-2 space-y-1 text-xs">
                    {g.items.map((i) => (
                      <li key={i}>• {i}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <Button asChild variant="outline" size="sm" className="mt-4">
              <Link to="/good-to-know">Full Bucharest guide</Link>
            </Button>
          </Section>

          <Section title="Booking timeline">
            <ol className="space-y-3">
              {history.map((h) => (
                <li key={h.id} className="flex items-start gap-3">
                  <span className="mt-1.5 size-2 shrink-0 rounded-full bg-gold" />
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {stageLabel(h.stage as BookingStage)}
                    </p>
                    <p className="text-xs">
                      {new Date(h.created_at).toLocaleString()}
                      {h.note ? ` · ${h.note}` : ""}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </Section>
        </section>
      </main>
    </>
  );
}
